import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { arePricesLoading, getDurationPackageOptions, isLPCAdvancedSortingEnabled, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import Pane from 'components/Common/Pane'
import { useAppSelector } from 'hooks/reduxHooks'
import { getDatesOccupancyLabel } from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelMobileVariantUtils'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import usePropertyBedbankViews from 'hooks/OfferPage/usePropertyBedbankViews'
import useOffer from 'hooks/Offers/useOffer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PropertyRoomOptionMobile from 'components/OfferPage/Property/MobileBookingFlow/PropertyRoomOptionMobile'
import uuidV4 from 'lib/string/uuidV4Utils'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { getCombinedRates, getPackageOptionsByRoom } from 'components/OfferPage/Property/SinglePropertyUtils'
import useQueryParams from 'hooks/useQueryParams'

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`

const RoomHeaderContainer = styled(Group)`
  padding: ${rem(20)};
  padding-top: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  room: App.PackageRoomType;
  selectedBedbankRoomId?: string;
  offer: App.HotelOffer;
  airports?: Array<App.Airport>;
  rooms: Array<App.Occupants>;
  checkIn?: string;
  duration?: number;
  previewMode: boolean;
  roomRateId?: string;
  pricesLoading: boolean;
  packageOptions: Array<OfferPackageOptionView>;
  onClose?: () => void;
  onBack?: () => void;
  label: string;
}

function SinglePropertyPackageSelectionModal(props: Props) {
  const {
    room,
    selectedBedbankRoomId,
    offer,
    airports,
    rooms,
    checkIn,
    duration,
    previewMode,
    roomRateId,
    pricesLoading,
    packageOptions,
    label,
    onClose,
    onBack,
  } = props

  const utmContent = useQueryParams().get('utm_content')

  const LPCAdvancedSortingEnabled = useAppSelector(isLPCAdvancedSortingEnabled)

  const [bedbankPackageViews, fetchingBedbankViews] = usePropertyBedbankViews(offer.property.id)
  const selectedPackageView = useMemo(() => bedbankPackageViews.find(view => view.package.roomId === selectedBedbankRoomId), [bedbankPackageViews, selectedBedbankRoomId])
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankOfferId)

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const packageOptionsByRoom = useMemo(() => getPackageOptionsByRoom(packageOptions, LPCAdvancedSortingEnabled, bedbankPackageViews, mapping), [packageOptions, LPCAdvancedSortingEnabled, bedbankPackageViews, mapping])

  const selectedRoom = useMemo(() => packageOptionsByRoom.find(option => option.bedbank.some(bedbank => bedbank.package.roomId === selectedBedbankRoomId) || option.lpc.some(lpc => lpc.package.roomType?.id === room?.id)), [packageOptionsByRoom, room?.id, selectedBedbankRoomId])

  const options = useMemo(() => {
    if (previewMode) {
      // Preview mode is for content people and will show unavailable rates so they can validate them
      // as the rates may not have had prices and such loaded yet
      return selectedRoom?.lpc
    }
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !pricesLoading
    return selectedRoom?.lpc.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [offer, selectedRoom, previewMode, pricesLoading])

  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options ?? [],
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
    },
  )

  const combinedRates = getCombinedRates(selectedRoom?.lpc ?? [], selectedRoom?.bedbank ?? [], availableOptions, previewMode, utmContent, roomRateId)

  // Single Property pages uses first the LPC roomType package name
  const roomName = useMemo(() => room?.name ?? selectedRoom?.lpc[0].package.roomType?.name ?? selectedPackageView?.package.name, [room, selectedRoom, selectedPackageView])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageOptionsByRoom]) // regenerate id if list of rooms changes

  return (
    <>
      <ModalHeader
        title="Choose your package"
        subtitle={label}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      />
      <ModalBody>
        <StyledModalContent>
          <Pane type="light">
            <Group direction="vertical" gap={16}>
              <RoomHeaderContainer direction="vertical" gap={4} noDisplayWhenEmpty>
                {roomName && <Heading variant="heading5">{roomName}</Heading>}
                {combinedRates.length > 0 && <Caption variant="large">{pluralizeToString('option', combinedRates.length)} available</Caption>}
              </RoomHeaderContainer>
              <Pane data-testid="rate-selectedRoom">
                {selectedRoom && <PropertyRoomOptionMobile
                  key={selectedRoom.roomId}
                  offer={offer}
                  packageViews={selectedRoom.lpc}
                  bedbankViews={selectedRoom.bedbank}
                  bedbankOffer={bedbankOffer}
                  flightDestination={destinationAirport}
                  roomListId={roomListId}
                />}
                {fetchingBedbankViews && <LoadingIndicator inline />}
              </Pane>
            </Group>
          </Pane>
        </StyledModalContent>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  selectedBedbankRoomId: state.selectedBedbankRoomId,
  offer: state.offer,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  airports: appState.geo.airports,
  label: getDatesOccupancyLabel(state),
  roomRateId: state.roomOption?.roomRateId,
  previewMode: appState.system.previewMode,
  pricesLoading: arePricesLoading(appState, state),
  packageOptions: getDurationPackageOptions(state, appState),
}))(SinglePropertyPackageSelectionModal)
